import { Button, ButtonLevel, ButtonSize, Text, TextColor } from '@vizit/atoms';
import { Dialog, DialogHeaderStatus, DialogHeaderVariant, type DialogProps } from '@vizit/molecules';
import * as React from 'react';

export enum RefreshPDPDialogVariant {
  Single = 'single',
  All = 'all',
}

interface RefreshPDPDialogProps extends Omit<DialogProps, 'children'> {
  /**
   * The variant of the dialog - single product or all products
   *
   * @optional
   * @type {RefreshPDPDialogVariant}
   * @default RefreshPDPDialogVariant.Single
   */
  variant?: RefreshPDPDialogVariant;
  /**
   * Content for the trigger button. If not provided, the button will not be rendered.
   *
   * @optional
   * @type {React.ReactNode}
   */
  triggerButtonContent?: React.ReactNode;
  /**
   * Callback when the refresh action is confirmed
   */
  onSubmit: () => Promise<void>;
  /**
   * The retailer display name shown in the single-product warning copy (e.g., "Amazon US", "Walmart US").
   * Defaults to "the retailer" when not provided. Ignored by the "all" variant, which spans multiple
   * retailers and always reads "their source retailers".
   */
  retailerDisplayName?: string;
}

/**
 * Dialog component for refreshing product data from the source (e.g., Amazon)
 * 2 variants with different copy for refreshing a single product vs all products
 *
 * @param props - The component props
 * @param props.variant - The variant of the dialog (single or all products)
 * @param props.onSubmit - Callback function when refresh is confirmed
 * @param props.isOpen - Whether the dialog is open
 * @param props.onOpenChange - Callback when dialog open state changes
 * @param props.triggerButtonContent - Content for the trigger button. If not provided, the button will not be rendered.
 *
 * @example
 * <RefreshPDPDialog
 *   variant={RefreshPDPDialogVariant.Single}
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 *   onSubmit={handleRefresh}
 * />
 */
function RefreshPDPDialog(props: RefreshPDPDialogProps) {
  const {
    variant = RefreshPDPDialogVariant.Single,
    isOpen,
    onOpenChange,
    onSubmit,
    triggerButtonContent,
    retailerDisplayName,
    ...other
  } = props;

  const retailerSource = retailerDisplayName || 'the retailer';

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleClose = React.useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleSubmit = React.useCallback(async () => {
    setIsSubmitting(true);
    onSubmit().finally(() => {
      setIsSubmitting(false);
    });
  }, [onSubmit]);

  const isAllVariant = variant === RefreshPDPDialogVariant.All;

  const triggerButton =
    triggerButtonContent != null ? (
      <Button level={ButtonLevel.Primary}>{triggerButtonContent}</Button>
    ) : (
      <Button className="sr-only" aria-hidden="true" isDisabled>
        Open dialog
      </Button>
    );

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} {...other}>
      {triggerButton}
      <Dialog.Overlay>
        <Dialog.Content dialogClassName="gap-4">
          {({ close }) => (
            <>
              <Dialog.Header variant={DialogHeaderVariant.Icon} status={DialogHeaderStatus.Error}>
                <Dialog.Title>{isAllVariant ? 'Refresh all product pages' : 'Refresh product page'}</Dialog.Title>
              </Dialog.Header>
              <div className="px-12 space-y-4 text-sm">
                <Text color={TextColor.Tertiary} showEllipsis={false}>
                  {isAllVariant ? (
                    <>
                      Refreshing these product pages will pull the latest content from their source retailers and will
                      overwrite changes you've made in Vizit.
                    </>
                  ) : (
                    <>
                      Refreshing this product page will pull the latest content from {retailerSource} and will overwrite
                      changes you've made in Vizit.
                    </>
                  )}
                </Text>
                <Text color={TextColor.Tertiary} showEllipsis={false}>
                  Are you sure you want to continue?
                </Text>
              </div>
              <Dialog.Footer>
                <Button
                  className="w-full"
                  size={ButtonSize.Lg}
                  level={ButtonLevel.Secondary}
                  onPress={onOpenChange ? handleClose : close}
                >
                  Cancel
                </Button>
                <Button
                  isDisabled={isSubmitting}
                  className="w-full"
                  size={ButtonSize.Lg}
                  level={ButtonLevel.Danger}
                  onPress={handleSubmit}
                >
                  {isAllVariant ? 'Refresh all' : 'Refresh page'}
                </Button>
              </Dialog.Footer>
            </>
          )}
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

export { RefreshPDPDialog };
export type { RefreshPDPDialogProps };
